// Libs
import debounce from 'debounce';
import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Droppable } from 'react-beautiful-dnd';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import {
  filter, cloneDeep, find, remove
} from 'lodash';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import { List } from 'react-virtualized';
import Checkbox from '@material-ui/core/Checkbox';
import classNames from 'classnames';
import { useFootBar } from '../../footbar/FootBarContext';
import { useModal } from '../../modal/ModalContext';

// Components
import IotRocketDraggable from './Draggable';
import TicketView from './TicketView';
import { getGroupNameFromId } from '../../groups/helper';

// Helper
import { filterTable } from '../../navbar/helper';
import { canSeeMobileBottomNavigation } from '../../users/helper';
import { customTicketFilterFunctions, customTicketFiltersByRole, selectedTicketsCount } from '../helper';
import { enableDeleteOnTicket } from '../helper';

const filterGroup = (filters, list, currentUserRole) => {
  const newFilters = cloneDeep(filters);
  let newList = cloneDeep(list);

  newFilters.forEach(paramFilter => {
    if (paramFilter.filterFunction) {
      newList = paramFilter.filterFunction(list);
    }
  });

  return filterTable(
    filter(filters, f => f.type !== 'defaultOptions'),
    newList,
    [],
    [],
    customTicketFiltersByRole[currentUserRole],
    customTicketFilterFunctions,
    true
  );
};


export default props => {
  const [t] = useTranslation();
  const modal = useModal();
  const filters = useSelector(state => state.navbar.filters);
  const currentUser = useSelector(state => state.users.currentUser);
  const tickets = filterGroup(filters, props.category.tickets, currentUser.workspace_role.workspace_role_name);
  const tickets2 = useSelector(state => state.tickets);
  const refContainer = useRef(null);
  const footbar = useFootBar();
  const groupList = useSelector(state => state.groups.list);
  const [ticketWidth, setTicketWidth] = useState(100);
  const [columnHeight, setColumnHeight] = useState(isMobile ? '80vh' : 500);
  const [selected, setSelected] = useState(false);
  const workspaceSettings = useSelector(state => state.workspace.settings);
  const history = useHistory();
  const users = useSelector(state => state.users.list);
  const devices = useSelector(state => state.devices.list);
  const ticketInDetailsPopup = useSelector(state => state.tickets.ticketShownInDetailsPopup);
  const selectedTickets = footbar.getList();
  const isSupervisorMode = currentUser.workspace_role
    && currentUser.workspace_role.workspace_role_name === 'User'
    && workspaceSettings.supervision_skills_to_users;
  const hasRightOnPriority = (currentUser.workspace_role?.workspace_role_name === 'User' && (workspaceSettings.is_user_rights_on_priority || false))
    || currentUser.workspace_role?.workspace_role_name !== 'User';
  const enableUpdateStatus = currentUser.workspace_role?.workspace_role_name !== 'User';
  const categories = tickets2.list.filter(category => category.name !== tickets.selectedStatusMobile && category.name !== 'ARCHIVED').map(category => category.name).join('___')
  const disabledDrag = currentUser.workspace_role?.workspace_role_name === 'User' || isMobile;

  const calcElementSize = () => {
    if (refContainer) {
      setTicketWidth(refContainer.current?.offsetWidth);
      setColumnHeight(refContainer.current?.offsetHeight);
    }
  };

  const toggleSelection = () => {
    const selectedTickets = cloneDeep(footbar.getList());
    if (selectedTickets
      .filter(obj => obj.status === props.category.name).length === tickets.length
      || footbar.getList().length === 0) {
      setSelected(!selected);
      tickets.forEach(ticket => (find(selectedTickets, selectedTicket => selectedTicket?.id === ticket?.id)
        ? remove(selectedTickets, selectedTicket => selectedTicket?.id === ticket?.id)
        : selectedTickets.push(ticket))
      );
    } else {
      tickets.forEach(ticket => {
        if (!find(selectedTickets, selectedTicket => selectedTicket.id === ticket.id)) {
          setSelected(true);
          selectedTickets.push(ticket);
        }
      });
    }
    footbar.update({
      selected: selectedTickets.map(selectedTicket => selectedTicket.id),
      list: selectedTickets,
      type: 'ticket'
    });
  };

  const selectedTicketsCountInColumn = selectedTicketsCount(selectedTickets, tickets);
  const areAllCardsSelected = selectedTicketsCountInColumn === tickets.length && tickets.length > 0;
  const areCardsPartlySelected = selectedTicketsCountInColumn > 0 && selectedTicketsCountInColumn < tickets.length;

  useEffect(calcElementSize, [refContainer]);
  window.addEventListener('resize', debounce(calcElementSize, 200));


  useEffect(() => {
    setSelected(!(
      footbar.getList().filter(obj => obj.status === props.category.name).length !== props.category.tickets.length
      || footbar.getList().filter(obj => obj.status === props.category.name).length === 0)
    );
  }, [footbar.getList(), props.category.tickets.length]);

  const canHaveCheckboxOnTicketsColumn = !isMobile && currentUser.workspace_role?.workspace_role_name !== 'User'

  return (
    <div className={'droppable-container'} ref={refContainer} style={{ width: props.width }}>
      <div className={'title'}>
        {/* No checkbox for user */}
        {canHaveCheckboxOnTicketsColumn && <Checkbox checked={areAllCardsSelected} indeterminate={areCardsPartlySelected} onChange={toggleSelection} color={'primary'} />}
        <h3>{t(`tickets:${props.category.name}`)}</h3>
        <span className={'ticket-number-in-col'}>{`(${tickets.length})`}</span>
      </div>
      <Droppable
        key={props.category.id}
        droppableId={props.category.id}
        mode="virtual"
        renderClone={(provided, snapshot, rubric) => {
          const assigners = tickets[rubric.source.index].assigners ?? ticketInDetailsPopup.assigners;
          return (
            <div
              {...provided.draggableProps}
              {...provided.dragHandleProps}
              ref={provided.innerRef}
            >
              <TicketView
                categories={categories}
                isDragging={snapshot.isDragging}
                ticket={tickets[rubric.source.index]}
                enableDelete={false}
                disabledDrag={disabledDrag}
                isSupervisorMode={isSupervisorMode}
                hasRightOnPriority={hasRightOnPriority}
                enableUpdateStatus={enableUpdateStatus}
                getFootBarList={footbar.getList}
                updateFootBar={footbar.update}
                closeFootBar={footbar.close}
                updateModal={modal.update}
                users={users}
                assigners={assigners.join('___')}
                skillName={getGroupNameFromId(groupList, tickets[rubric.source.index]?.custom_field?.skill_group_id)}
                groupBatimentName={groupList.find(g => g.group_id === tickets[rubric.source.index]?.custom_field?.batiment_group_id)?.group_name}
                groupFloorName={groupList.find(g => g.group_id === tickets[rubric.source.index]?.custom_field?.floor_group_id)?.group_name}
                groupPositionName={groupList.find(g => g.group_id === tickets[rubric.source.index]?.custom_field?.position_group_id)?.group_name}
              />
            </div>
          )
        }}
      >
        {provided => (
          <div ref={provided.innerRef} className={classNames('column-container', canSeeMobileBottomNavigation(currentUser, history.location.pathname, devices, workspaceSettings) ? 'column-with-bottom-navigation' : '')}>
            <List
              height={columnHeight}
              rowCount={tickets.length}
              rowHeight={isMobile ? 121 : 145}
              outerRef={provided.innerRef}
              width={ticketWidth}
              itemData={tickets}
              ref={ref => {
                // react-virtualized has no way to get the list's ref that I can so
                // So we use the `ReactDOM.findDOMNode(ref)` escape hatch to get the ref
                if (ref) {
                  // eslint-disable-next-line react/no-find-dom-node
                  const whatHasMyLifeComeTo = ReactDOM.findDOMNode(ref);
                  if (whatHasMyLifeComeTo instanceof HTMLElement) {
                    provided.innerRef(whatHasMyLifeComeTo);
                  }
                }
              }}
              style={{
                transition: 'background-color 0.2s ease'
              }}
              /* eslint-disable-next-line max-len */
              rowRenderer={({ index, style }) => {
                const enableDelete = enableDeleteOnTicket(currentUser, tickets[index], workspaceSettings)
                const assigners = tickets[index].assigners ?? ticketInDetailsPopup.assigners;
                return (
                  <IotRocketDraggable
                    key={index}
                    index={index}
                    style={style}
                    categories={categories}
                    ticket={tickets[index]}
                    enableDelete={enableDelete}
                    disabledDrag={disabledDrag}
                    isSupervisorMode={isSupervisorMode}
                    hasRightOnPriority={hasRightOnPriority}
                    enableUpdateStatus={enableUpdateStatus}
                    getFootBarList={footbar.getList}
                    updateFootBar={footbar.update}
                    closeFootBar={footbar.close}
                    updateModal={modal.update}
                    users={users}
                    assigners={assigners.join('___')}
                    skillName={getGroupNameFromId(groupList, tickets[index]?.custom_field?.skill_group_id)}
                    groupBatimentName={groupList.find(g => g.group_id === tickets[index]?.custom_field?.batiment_group_id)?.group_name}
                    groupFloorName={groupList.find(g => g.group_id === tickets[index]?.custom_field?.floor_group_id)?.group_name}
                    groupPositionName={groupList.find(g => g.group_id === tickets[index]?.custom_field?.position_group_id)?.group_name}
                  />
                );
              }}
            />
          </div>
        )}
      </Droppable>
    </div>
  );
};
